import { Button, Dialog } from '@cloudflare/kumo';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';
import { api, queryClient, unwrap } from '../api.ts';
import { ErrorNotice, Loading } from '../common.tsx';
import { BillingPayment } from './billing-payment.tsx';
import { PaymentMethodSetup } from './payment-method-setup.tsx';

export function SiteActivation({
  siteId,
  accountId,
  onActivated,
  onClose,
}: {
  siteId: string;
  accountId: string;
  onActivated: () => Promise<unknown>;
  onClose: () => void;
}) {
  const notified = useRef(false);
  const [collectPayment, setCollectPayment] = useState<boolean>();
  const [paymentPending, setPaymentPending] = useState(false);
  const billing = useQuery({
    queryKey: ['billing', accountId],
    queryFn: async () =>
      unwrap(
        await api.GET('/api/accounts/{accountUserId}/billing', {
          params: { path: { accountUserId: accountId } },
        }),
      ),
    refetchInterval: (query) =>
      query.state.data?.operations.some((operation) =>
        ['Pending', 'Processing', 'AwaitingPaymentMethod', 'AwaitingPayment'].includes(operation.status),
      )
        ? 2000
        : false,
  });
  const refresh = useMutation({
    mutationFn: async (operationId: string) =>
      unwrap(
        await api.POST('/api/accounts/{accountUserId}/billing/{operationId}/refresh', {
          params: { path: { accountUserId: accountId, operationId } },
          body: {},
        }),
      ),
    onSettled: () => queryClient.invalidateQueries({ queryKey: ['billing', accountId] }),
  });
  const activate = useMutation({
    mutationFn: async () =>
      unwrap(
        await api.POST('/api/sites/{siteId}/service', {
          params: { path: { siteId } },
          body: { idempotencyKey: crypto.randomUUID() },
        }),
      ),
    onSettled: () => queryClient.invalidateQueries({ queryKey: ['billing', accountId] }),
  });
  const retry = useMutation({
    mutationFn: async (operationId: string) =>
      unwrap(
        await api.POST('/api/accounts/{accountUserId}/billing/{operationId}/retry', {
          params: { path: { accountUserId: accountId, operationId } },
          body: {},
        }),
      ),
    onSettled: () => queryClient.invalidateQueries({ queryKey: ['billing', accountId] }),
  });
  const info = billing.data;
  const site = info?.sites.find((entry) => entry.id === siteId);
  const covered = Boolean(site && !['Inactive', 'Pending'].includes(site.status));
  const pending =
    info?.operations.filter(
      (operation) =>
        operation.siteId === siteId &&
        operation.kind === 'Activate' &&
        ['Pending', 'Processing', 'AwaitingPaymentMethod', 'AwaitingPayment'].includes(operation.status),
    ) ?? [];
  if (info && collectPayment === undefined) setCollectPayment(!info.paymentMethod);
  const publish = useMutation({ mutationFn: onActivated });
  const publishSite = publish.mutate;
  useEffect(() => {
    if (!covered || notified.current) return;
    notified.current = true;
    publishSite();
  }, [covered, publishSite]);
  const busy =
    activate.isPending || paymentPending || refresh.isPending || retry.isPending || publish.isPending;
  const submitLabel = info?.trialEligible
    ? 'Start trial and publish'
    : info?.stripeSubscriptionStatus === 'Trialing'
      ? 'Join trial and publish'
      : 'Activate and publish · $15/month';
  const dismiss = () => {
    if (!busy && !billing.isPending) onClose();
  };
  return (
    <Dialog.Root
      open
      onOpenChange={(open) => {
        if (!open) dismiss();
      }}
    >
      <Dialog
        size="lg"
        className="p-6 grid gap-5 max-h-[85dvh] overflow-auto [&_.loading]:px-0"
        aria-describedby={undefined}
      >
        <Dialog.Title className="text-xl font-semibold">Activate and publish</Dialog.Title>
        <ErrorNotice
          error={billing.error ?? activate.error ?? refresh.error ?? retry.error ?? publish.error}
        />
        {billing.isPending ? (
          <Loading />
        ) : !info ? null : !info.owner ? (
          <p>Only the account owner can activate this site.</p>
        ) : !site ? (
          <p>This site is not on this billing account.</p>
        ) : covered ? (
          publish.error ? (
            <Button variant="primary" onClick={() => publish.mutate()}>
              Retry publishing
            </Button>
          ) : (
            <Loading label="Publishing…" />
          )
        ) : (
          <>
            <p className="text-kumo-subtle leading-relaxed">
              {info.trialEligible
                ? 'Three days free, then $15/month per site. A payment method is required.'
                : info.stripeSubscriptionStatus === 'Trialing'
                  ? `This site will join your current trial${info.trialEndsAt ? `, ending ${new Date(info.trialEndsAt).toLocaleDateString()}` : ''}, then cost $15/month.`
                  : info.periodEndsAt
                    ? `This site costs $15/month. When you activate, the first charge covers only the rest of the current billing period, through ${new Date(info.periodEndsAt).toLocaleDateString()}.`
                    : '$15/month for this site. Your first payment is collected when you activate.'}
            </p>
            {pending.length === 0 ? (
              !info.paymentMethod || collectPayment ? (
                <PaymentMethodSetup
                  accountId={accountId}
                  submitLabel={submitLabel}
                  onPendingChange={setPaymentPending}
                  onSaved={() => {
                    setPaymentPending(false);
                    setCollectPayment(false);
                    activate.mutate();
                  }}
                />
              ) : (
                <Button variant="primary" disabled={busy} onClick={() => activate.mutate()}>
                  {activate.isPending ? 'Activating…' : submitLabel}
                </Button>
              )
            ) : (
              pending.map((operation) => (
                <div key={operation.id} className="grid gap-4">
                  {operation.error ? <p className="text-kumo-subtle text-sm">{operation.error}</p> : null}
                  {['AwaitingPaymentMethod', 'AwaitingPayment'].includes(operation.status) ? (
                    <BillingPayment
                      key={`${operation.id}:${operation.status}:${operation.invoiceId ?? ''}`}
                      accountId={accountId}
                      operationId={operation.id}
                      invoiceId={operation.invoiceId ?? undefined}
                      submitLabel={submitLabel}
                      disabled={retry.isPending}
                      onPendingChange={setPaymentPending}
                      onComplete={() => refresh.mutate(operation.id)}
                    />
                  ) : (
                    <Loading label="Activating…" />
                  )}
                  {operation.status === 'AwaitingPayment' ? (
                    <Button size="sm" onClick={() => retry.mutate(operation.id)} disabled={busy}>
                      Retry payment
                    </Button>
                  ) : null}
                </div>
              ))
            )}
          </>
        )}
        <Button
          variant="ghost"
          className="justify-self-start"
          onClick={dismiss}
          disabled={busy || billing.isPending}
        >
          Close
        </Button>
      </Dialog>
    </Dialog.Root>
  );
}
